import "./TeamPanel.css";
import { useEffect, useRef, useState } from "react";
import Card, { CardHead } from "../Card/Card";
import TextField from "../TextField/TextField";
import Button from "../Button/Button";
import Avatar from "../Avatar/Avatar";

function Member(props) {
  return (
    <li className="member row">
      <Avatar src={props.avatarUrl} name={props.name} size="sm" />
      <span className="member-name t-label">{props.name}</span>
      <span className="member-email t-mono">{props.email}</span>
      {props.isYou ? <span className="member-you t-eyebrow">You</span> : null}
    </li>
  );
}

function LeaveConfirm(props) {
  const cancelRef = useRef(null);

  useEffect(() => {
    cancelRef.current && cancelRef.current.focus();
  }, []);

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") props.onCancel();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [props]);

  return (
    <div className="leave-confirm col">
      <p className="t-body-sm t-dim">
        {props.solo
          ? "Are you sure? You are the only member, so leaving deletes your team and everything submitted with it."
          : "Are you sure? You will lose your seat on this team."}
      </p>
      <div className="leave-confirm-actions row">
        <Button
          variant="ghost"
          small
          ref={cancelRef}
          onClick={props.onCancel}
          disabled={props.busy}
        >
          Cancel
        </Button>
        <Button
          variant="secondary"
          small
          busy={props.busy}
          busyLabel="Leaving…"
          onClick={props.onConfirm}
        >
          Yes, leave
        </Button>
      </div>
      {props.error ? (
        <p className="t-body-sm leave-confirm-error" role="alert">
          {props.error}
        </p>
      ) : null}
    </div>
  );
}

export default function TeamPanel(props) {
  const { team, user, api, rosterOpen, closesLabel } = props;
  const showAvatars = props.showAvatars !== false;
  const [copied, setCopied] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [leaveError, setLeaveError] = useState("");

  async function copy() {
    try {
      await navigator.clipboard.writeText(team.code);
    } catch (e) {
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  }

  async function confirmLeave() {
    setLeaving(true);
    setLeaveError("");
    try {
      await api.leaveTeam();
    } catch (e) {
      setLeaveError(e.message);
    } finally {
      setLeaving(false);
    }
  }

  const open = 4 - team.members.length;

  return (
    <Card className="team">
      <CardHead
        eyebrow={rosterOpen ? "Open" : "Final"}
        title={team.name}
        description={
          rosterOpen
            ? closesLabel
              ? `Anyone can join or leave until ${closesLabel}.`
              : "Anyone can join or leave while the window is open."
            : null
        }
      />

      <ul className="member-list col" aria-label="Team members">
        {team.members.map((m) => (
          <Member
            key={m.email}
            avatarUrl={showAvatars ? m.avatarUrl : null}
            name={m.name}
            email={m.email}
            isYou={m.email === user.email}
          />
        ))}
      </ul>

      {rosterOpen && open > 0 ? (
        <p className="team-slots t-body-sm t-dimmer">
          {open === 1 ? "1 slot" : `${open} slots`} available on {team.name}.
        </p>
      ) : null}

      {rosterOpen ? (
        <div className="team-code col">
          <span className="team-code-label t-eyebrow">Join code</span>
          <div className="team-code-row row">
            <code className="team-code-value t-mono-lg">{team.code}</code>
            <Button variant="secondary" small onClick={copy}>
              {copied ? "Copied" : "Copy"}
            </Button>
          </div>
          <p className="t-body-sm t-dimmer">
            Share this with your teammates. Anyone with the code can join while
            the window is open.
          </p>
        </div>
      ) : null}

      {rosterOpen ? (
        <div className="team-foot col">
          {confirming ? (
            <LeaveConfirm
              solo={team.members.length === 1}
              busy={leaving}
              error={leaveError}
              onCancel={() => {
                setConfirming(false);
                setLeaveError("");
              }}
              onConfirm={confirmLeave}
            />
          ) : (
            <div className="row">
              <Button variant="ghost" small onClick={() => setConfirming(true)}>
                Leave team
              </Button>
            </div>
          )}
        </div>
      ) : null}
    </Card>
  );
}

export function TeamGate(props) {
  const { api } = props;
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [nameError, setNameError] = useState("");
  const [codeError, setCodeError] = useState("");
  const [creating, setCreating] = useState(false);
  const [joining, setJoining] = useState(false);

  async function create() {
    setCreating(true);
    setNameError("");
    try {
      await api.createTeam(name);
    } catch (e) {
      setNameError(e.message);
    } finally {
      setCreating(false);
    }
  }

  async function join() {
    setJoining(true);
    setCodeError("");
    try {
      await api.joinTeam(code);
    } catch (e) {
      setCodeError(e.message);
    } finally {
      setJoining(false);
    }
  }

  return (
    <div className="gate col">
      <div className="gate-head col">
        <span className="t-eyebrow t-brand">Step one</span>
        <h1 className="t-h1">Get yourself a team</h1>
        <p className="t-body t-dim">
          Teams are up to four people. Start one and share the code, or join a
          team that already has room.
        </p>
        {props.closesLabel ? (
          <span className="gate-window t-mono">
            Team creation closes {props.closesLabel}
          </span>
        ) : null}
      </div>

      <div className="gate-options grid grid-2">
        <Card className="gate-card">
          <CardHead eyebrow="Option 1" accent title="Create a team" />
          <TextField
            label="Team name"
            value={name}
            onChange={(v) => {
              setName(v);
              if (nameError) setNameError("");
            }}
            onSubmit={create}
            placeholder="Your team name"
            error={nameError}
            help="You'll get a code to share with up to three teammates."
          />
          <Button block busy={creating} busyLabel="Creating…" onClick={create}>
            Create team
          </Button>
        </Card>

        <Card className="gate-card">
          <CardHead eyebrow="Option 2" title="Join a team" />
          <TextField
            label="Join code"
            value={code}
            onChange={(v) => {
              setCode(v.toUpperCase());
              if (codeError) setCodeError("");
            }}
            onSubmit={join}
            placeholder="MULT-____"
            error={codeError}
            help="Four characters after MULT-, from a teammate. Try MULT-7K2Q."
            mono
          />
          <Button
            variant="secondary"
            block
            busy={joining}
            busyLabel="Joining…"
            onClick={join}
          >
            Join team
          </Button>
        </Card>
      </div>
    </div>
  );
}
